import { useEffect } from 'react';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import type { FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { string, object } from 'zod/v4';

import Toast from '@/components/ui/Toast';
import { appConfig } from '@/config/appConfig';
import appConstants from '@/constants/appConstants';
import { getValidationErrorMessage } from '@/helpers';
import useI18n from '@/hooks/useI18n';
import useNavigation from '@/hooks/useNavigation';
import usePost from '@/hooks/usePost';
import type { ErrorResponse } from '@/types/ErrorResponse';
import type { SuccessResponse } from '@/types/SuccessResponse';

import { authRoutes } from '../routes/authRoutes';
import type { ForgotPasswordRequestBody } from '../types/ForgotPasswordRequestBody';

import useAuthStore from './useAuthStore';

export type FormValues = {
    identifier: string;
};

type UseForgotPasswordReturn = {
    isValid: boolean;
    isLoading: boolean;
    response: SuccessResponse | null;
    error: ErrorResponse | null;
    validationErrors: FieldErrors<FormValues>;
    submit: () => void;
    register: UseFormRegister<FormValues>;
    setValue: UseFormSetValue<FormValues>;
    watch: UseFormWatch<FormValues>;
};

export default function useForgotPassword(): UseForgotPasswordReturn {
    // ----------------------------------------------------------------------------------------------------
    // MARK: States & Constants
    // ----------------------------------------------------------------------------------------------------
    const { t } = useI18n();
    const { replace } = useNavigation();

    const updateTempForgotPasswordData = useAuthStore((state) => state.updateTempForgotPasswordData);
    const { submit: submitHandler, response, isLoading, error, validationErrors } = usePost<SuccessResponse, ErrorResponse>();

    const schema = object({
        identifier: string({ error: t('Auth.Validations.Identifier_Required') })
            .min(4, { error: t('Auth.Validations.Identifier_Minimum_Length') })
            .max(255, { error: t('Auth.Validations.Identifier_Maximum_Length') }),
    });

    const {
        register,
        formState: { errors, isValid },
        setError,
        watch,
        setValue,
    } = useForm({
        resolver: standardSchemaResolver(schema),
        mode: appConfig.formValidation.mode,
        defaultValues: {
            identifier: '',
        },
    });

    const { identifier } = watch();

    // ----------------------------------------------------------------------------------------------------
    // MARK: Functions
    // ----------------------------------------------------------------------------------------------------
    function submit(): void {
        const requestBody: ForgotPasswordRequestBody = {
            identifier,
        };

        submitHandler(authRoutes.api.forgotPassword(), requestBody, {
            onSuccess: (res) => {
                Toast.success({ message: res.message });
                updateTempForgotPasswordData(requestBody);
                localStorage.setItem(appConstants.otpCountdownStorageName, appConfig.env.otpCountdown.toString());
                replace(authRoutes.app.forgotPasswordVerification());
            },
        });
    }

    // ----------------------------------------------------------------------------------------------------
    // MARK: Side Effects
    // ----------------------------------------------------------------------------------------------------
    useEffect(() => {
        if (validationErrors.length > 0) {
            setError('identifier', { message: getValidationErrorMessage('identifier', validationErrors) });
        }
    }, [validationErrors, setError]);

    // ----------------------------------------------------------------------------------------------------
    // MARK: Return
    // ----------------------------------------------------------------------------------------------------
    return {
        isValid,
        isLoading,
        response,
        error,
        validationErrors: errors,
        submit,
        register,
        setValue,
        watch,
    };
}
